/* eslint-disable @typescript-eslint/no-explicit-any, no-console */

import * as Sentry from "@sentry/browser";
import { Severity } from "@sentry/browser";
import { Vue as VueSentryIntegration } from "@sentry/integrations/dist/vue";

import Vue from "vue";

import { LogLevel, LogLevelLabel, LogLevels } from "@/types";

export interface NjBreadcrumb {
  data?: any;
  category: string;
  message: string;
  level: LogLevelLabel;
  type: string;
}

/**
 * Custom logging service that utilizes local state
 *
 * TODO: debug mode
 */
export class LogService {
  private static _initialized = false;
  private static _isSentryEnabled = false;
  private static _isSentryLoaded = false;
  private static _sentryLogLevel: LogLevel = LogLevels.None;
  private static _consoleLogLevel: LogLevel = LogLevels.None;
  private static _preInitQueue: { logLevel: LogLevel; message: string }[] = [];

  static set consoleLogLevel(logLevel: LogLevel) {
    this._consoleLogLevel = logLevel;
    this.debug(`LogService.consoleLogLevel updated to '${logLevel.label}'`);
  }

  // TODO: remove when deleting /debug page
  static get consoleLogLevel(): LogLevel {
    return this._consoleLogLevel;
  }

  static set sentryLogLevel(sentryLogLevel: LogLevel) {
    this._sentryLogLevel = sentryLogLevel;
    this.debug(
      `LogService.sentryLogLevel updated to '${sentryLogLevel.label}'`
    );
  }

  // TODO: remove when deleting /debug page
  static get sentryLogLevel(): LogLevel {
    return this._sentryLogLevel;
  }

  static get isSentryLoaded(): boolean {
    return this._isSentryLoaded;
  }

  static _initialize(): void {
    if (this._initialized) {
      return;
    }
    this.sentryLogLevel = LogLevels.fromString(
      process.env.VUE_APP_SENTRY_LOG_LEVEL || "error"
    );
    this.consoleLogLevel = LogLevels.fromString(
      process.env.VUE_APP_CONSOLE_LOG_LEVEL || "error"
    );

    this.debug("Initializing LogService.");

    // Initialize Sentry if a valid DSN is provided
    this._isSentryEnabled = (process.env.VUE_APP_SENTRY_SDK_DSN || "").includes(
      "http"
    );
    if (this._isSentryEnabled) {
      Sentry.init({
        dsn: process.env.VUE_APP_SENTRY_SDK_DSN,
        environment: process.env.VUE_APP_SENTRY_ENVIRONMENT,
        integrations: [new VueSentryIntegration({ Vue, attachProps: true })],
        release: process.env.VUE_APP_SENTRY_RELEASE || undefined,
      });
      this._isSentryLoaded = true;
      this.info("Sentry is initialized.");
    }

    this._initialized = true;
    this._purgePreInitQueue();
    this.info("LogService is initialized.");
  }

  private static _purgePreInitQueue(): void {
    this.debug(`LogService._purgePreInitQueue started.`);
    for (const logItem of this._preInitQueue) {
      this._log(logItem.logLevel, logItem.message);
    }
    this.debug(`LogService._purgePreInitQueue is complete.`);
  }

  private static _log(
    logLevel: LogLevel,
    message: string,
    error?: Error,
    extra?: Record<string, unknown>
  ): void {
    if (!this._initialized) {
      this._preInitQueue.push({ logLevel, message });
      return;
    }

    const dateObj = new Date();
    const date = dateObj.toLocaleDateString();
    const time = dateObj.toLocaleTimeString();
    const dateTime = `[${date} - ${time}]`;

    // If enabled, log to console
    if (
      LogLevels.greaterThan(logLevel, LogLevels.None) &&
      LogLevels.lessThanOrEqual(logLevel, this._consoleLogLevel)
    ) {
      switch (logLevel) {
        case LogLevels.Fatal:
          console.error(dateTime, "Fatal:", message, error || "");
          break;
        case LogLevels.Error:
          console.error(dateTime, "Error:", message, error || "");
          break;
        case LogLevels.Warn:
          console.warn(dateTime, "Warning:", message);
          break;
        case LogLevels.Info:
          console.info(dateTime, "Info:", message);
          break;
        case LogLevels.Debug:
          console.debug(dateTime, "Debug:", message);
          break;
        case LogLevels.Trace:
          console.trace(dateTime, "Trace:", message);
          break;
        default:
      }
    }

    // If enabled, log to Sentry
    if (
      this._isSentryLoaded &&
      LogLevels.greaterThan(logLevel, LogLevels.None) &&
      LogLevels.lessThanOrEqual(logLevel, this._sentryLogLevel)
    ) {
      if (error) {
        // If an Error object is provided, use captureException for proper stack traces
        Sentry.captureException(error, {
          extra: { message, ...extra },
        });
      } else {
        // Otherwise just capture a message
        Sentry.captureMessage(
          message,
          // All LogLevels string values are supported by Sentry, except Trace (doesn't exist).
          Severity.fromString(
            LogLevels.equals(logLevel, LogLevels.Trace)
              ? "debug"
              : logLevel.label
          )
        );
      }
    }
  }

  // ----- PUBLIC FUNCTIONS ----- //

  public static fatal(
    message: string,
    error?: Error,
    extra?: Record<string, unknown>
  ): void {
    this._log(LogLevels.Fatal, message, error, extra);
  }

  public static error(
    message: string,
    error?: Error,
    extra?: Record<string, unknown>
  ): void {
    this._log(LogLevels.Error, message, error, extra);
  }

  public static warn(message: string): void {
    this._log(LogLevels.Warn, message);
  }

  public static info(message: string): void {
    this._log(LogLevels.Info, message);
  }

  public static log(message: string): void {
    this._log(LogLevels.Info, message);
  }

  public static debug(message: string): void {
    this._log(LogLevels.Debug, message);
  }

  public static trace(message: string): void {
    this._log(LogLevels.Trace, message);
  }

  public static addBreadcrumb(breadcrumb: NjBreadcrumb): void {
    const { level } = breadcrumb;
    const l = Severity.fromString(level);
    Sentry.addBreadcrumb({ ...breadcrumb, level: l });
  }
}
